import type { Element } from '../../format/form-definition';
import type { InsertionPosition } from '../elementInsertion';
import { insertElement, updateElement, withKey, withoutUndefined } from './tree';
import { changed, EditFunctions, refused } from './editContracts';
import { findElement, findField } from '../../format/elements';
import { sameJson } from '../same-json';
import { isEmpty, omitBy } from 'lodash';
import { canResizeField, defaultFieldWidthOf, fieldTypeOf, fitsFieldType } from '../../field-types';
import type { FieldWidth } from '../../format/fieldWidth';
import { AUTHOR_TEXTS } from '../../texts/author';
import { elementName } from '../../texts/words';

export type ElementTextKey = 'label' | 'title' | 'text' | 'description';

export type ElementEdit =
  | { kind: 'addElement'; element: Element; at: InsertionPosition }
  | {
      kind: 'setElementText';
      elementId: string;
      key: ElementTextKey;
      text: string;
    }
  | {
      kind: 'setFieldSettings';
      fieldId: string;
      settings: Record<string, unknown>;
    }
  | { kind: 'setDefaultValue'; fieldId: string; value: unknown }
  | { kind: 'setFieldWidth'; fieldId: string; width: FieldWidth };

export const elementEdits: EditFunctions<ElementEdit> = {
  addElement: (form, { element, at }) => changed(insertElement(form, element, at)),

  setFieldWidth: (form, { fieldId, width }) => {
    const field = findField(form, fieldId);
    if (!field || !canResizeField(field)) return changed(form);
    const next = width === defaultFieldWidthOf(field) ? undefined : width;
    if (field.styles?.width === next) return changed(form);
    const styles = withoutUndefined({ ...field.styles, width: next });
    return changed(
      updateElement(form, fieldId, found => withKey(found, 'styles', isEmpty(styles) ? undefined : styles)),
    );
  },

  setElementText: (form, { elementId, key, text }) => {
    const element = findElement(form, elementId);
    if (!element) return changed(form);
    const value = key === 'description' && text === '' ? undefined : text;
    if ((element as Partial<Record<ElementTextKey, string>>)[key] === value) return changed(form);
    return changed(updateElement(form, elementId, found => withKey(found, key, value)));
  },

  setFieldSettings: (form, { fieldId, settings }) => {
    const field = findField(form, fieldId);
    if (!field) return changed(form);
    const defaults = fieldTypeOf(field).defaults as Record<string, unknown>;
    const kept = omitBy(
      { ...field.settings, ...settings },
      (value, key) => value === undefined || sameJson(value, defaults[key]),
    );
    const next = isEmpty(kept) ? undefined : kept;
    if (sameJson(next, field.settings)) return changed(form);
    return changed(updateElement(form, fieldId, found => withKey(found, 'settings', next)));
  },

  setDefaultValue: (form, { fieldId, value }) => {
    const field = findField(form, fieldId);
    if (!field) return changed(form);
    const card = fieldTypeOf(field);
    if (!card.hasDefaultValue) return refused(AUTHOR_TEXTS.defaultValue.noDefault(elementName(field)));
    if (!fitsFieldType(field, value)) return refused(AUTHOR_TEXTS.defaultValue.cantHold(elementName(field)));
    const next = card.isEmpty(value) ? undefined : value;
    if (sameJson(next, field.defaultValue)) return changed(form);
    return changed(updateElement(form, fieldId, found => withKey(found, 'defaultValue', next)));
  },
};
